import React, { useState, useEffect, useRef } from 'react';
import { 
  TrendingUp, 
  PieChart, 
  BarChart3, 
  FileText, 
  Download, 
  Calendar,
  RefreshCw,
  ChevronDown
} from 'lucide-react';

interface RevenueData {
  month: string;
  roomRevenue: number;
  foodRevenue: number;
  otherRevenue: number;
  totalRevenue: number;
}

interface ExpenseData {
  category: string;
  amount: number;
  percentage: number;
  color: string;
}

interface PLData {
  revenue: {
    roomRevenue: number;
    foodRevenue: number;
    spaRevenue: number;
    otherRevenue: number;
    totalRevenue: number;
  };
  expenses: {
    payroll: number;
    utilities: number;
    maintenance: number;
    marketing: number;
    supplies: number;
    insurance: number;
    otherExpenses: number;
    totalExpenses: number;
  };
  profit: {
    grossProfit: number;
    netProfit: number;
    profitMargin: number;
  };
}

const FinanceAndAccounting: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [isLoading, setIsLoading] = useState(false);
  const [activeReport, setActiveReport] = useState('overview');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Different data sets for different periods
  const getFinancialData = (period: string) => {
    const dataSets = {
      week: {
        revenueData: [
          { day: 'Mon', roomRevenue: 45000, foodRevenue: 18000, otherRevenue: 5000, totalRevenue: 68000 },
          { day: 'Tue', roomRevenue: 52000, foodRevenue: 22000, otherRevenue: 6000, totalRevenue: 80000 },
          { day: 'Wed', roomRevenue: 48000, foodRevenue: 19000, otherRevenue: 5500, totalRevenue: 72500 },
          { day: 'Thu', roomRevenue: 55000, foodRevenue: 25000, otherRevenue: 7000, totalRevenue: 87000 },
          { day: 'Fri', roomRevenue: 62000, foodRevenue: 28000, otherRevenue: 8000, totalRevenue: 98000 },
          { day: 'Sat', roomRevenue: 68000, foodRevenue: 32000, otherRevenue: 9000, totalRevenue: 109000 },
          { day: 'Sun', roomRevenue: 45000, foodRevenue: 18000, otherRevenue: 5000, totalRevenue: 68000 }
        ]
      },
      month: {
        revenueData: [
          { week: 'W1', roomRevenue: 650000, foodRevenue: 240000, otherRevenue: 80000, totalRevenue: 970000 },
          { week: 'W2', roomRevenue: 720000, foodRevenue: 280000, otherRevenue: 90000, totalRevenue: 1090000 },
          { week: 'W3', roomRevenue: 680000, foodRevenue: 260000, otherRevenue: 85000, totalRevenue: 1025000 },
          { week: 'W4', roomRevenue: 750000, foodRevenue: 300000, otherRevenue: 95000, totalRevenue: 1145000 }
        ]
      },
      quarter: {
        revenueData: [
          { month: 'Month 1', roomRevenue: 2800000, foodRevenue: 1100000, otherRevenue: 350000, totalRevenue: 4250000 },
          { month: 'Month 2', roomRevenue: 2950000, foodRevenue: 1200000, otherRevenue: 380000, totalRevenue: 4530000 },
          { month: 'Month 3', roomRevenue: 3100000, foodRevenue: 1300000, otherRevenue: 400000, totalRevenue: 4800000 }
        ]
      },
      year: {
        revenueData: [
          { quarter: 'Q1', roomRevenue: 8500000, foodRevenue: 3200000, otherRevenue: 1000000, totalRevenue: 12700000 },
          { quarter: 'Q2', roomRevenue: 9200000, foodRevenue: 3600000, otherRevenue: 1100000, totalRevenue: 13900000 },
          { quarter: 'Q3', roomRevenue: 9800000, foodRevenue: 3800000, otherRevenue: 1200000, totalRevenue: 14800000 },
          { quarter: 'Q4', roomRevenue: 10500000, foodRevenue: 4000000, otherRevenue: 1300000, totalRevenue: 15800000 }
        ]
      }
    };
    return dataSets[period as keyof typeof dataSets] || dataSets.month;
  };

  const currentData = getFinancialData(selectedPeriod);
  const revenueData = currentData.revenueData;

  const periodOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const expenseData: ExpenseData[] = [
    { category: 'Payroll & Benefits', amount: 850000, percentage: 35.2, color: 'bg-blue-500' },
    { category: 'Utilities', amount: 180000, percentage: 7.5, color: 'bg-green-500' },
    { category: 'Maintenance', amount: 220000, percentage: 9.1, color: 'bg-yellow-500' },
    { category: 'Marketing', amount: 150000, percentage: 6.2, color: 'bg-purple-500' },
    { category: 'Supplies', amount: 120000, percentage: 5.0, color: 'bg-red-500' },
    { category: 'Insurance', amount: 80000, percentage: 3.3, color: 'bg-indigo-500' },
    { category: 'Other Expenses', amount: 810000, percentage: 33.7, color: 'bg-gray-500' }
  ];

  const plData: PLData = {
    revenue: {
      roomRevenue: 17500000,
      foodRevenue: 6800000,
      spaRevenue: 1200000,
      otherRevenue: 1500000,
      totalRevenue: 27000000
    },
    expenses: {
      payroll: 10200000,
      utilities: 2160000,
      maintenance: 2640000,
      marketing: 1800000,
      supplies: 1440000,
      insurance: 960000,
      otherExpenses: 9720000,
      totalExpenses: 30000000
    },
    profit: {
      grossProfit: 20200000,
      netProfit: -3000000,
      profitMargin: -11.1
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const handleExport = () => {
    // Mock export functionality
    console.log('Exporting financial data...');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Finance & Accounting</h2>
              <p className="text-sm text-gray-600">Detailed Financial Reports & Analytics</p>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 cursor-pointer min-w-[140px]"
                >
                  <span>{periodOptions.find(option => option.value === selectedPeriod)?.label}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                    {periodOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedPeriod(option.value);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors duration-150 ${
                          selectedPeriod === option.value
                            ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={handleExport}
              className="flex items-center space-x-2 px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export Report</span>
            </button>
            <button 
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors border border-gray-300"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Financial Reports</h3>
          <span className="text-sm text-gray-500">Select a report type to view detailed analytics</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setActiveReport('overview')}
            className={`group relative p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg ${
              activeReport === 'overview'
                ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 shadow-md'
                : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
            }`}
          >
            <div className="flex flex-col items-center space-y-2">
              <div className={`p-2 rounded-lg transition-colors ${
                activeReport === 'overview' 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-600'
              }`}>
                <BarChart3 className="w-5 h-5" />
              </div>
              <span className="text-sm font-semibold">Overview</span>
              <span className="text-xs text-gray-500">Financial Summary</span>
            </div>
            {activeReport === 'overview' && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full"></div>
            )}
          </button>
          
          <button
            onClick={() => setActiveReport('revenue')}
            className={`group relative p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg ${
              activeReport === 'revenue'
                ? 'border-green-500 bg-gradient-to-br from-green-50 to-green-100 text-green-700 shadow-md'
                : 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:bg-green-50'
            }`}
          >
            <div className="flex flex-col items-center space-y-2">
              <div className={`p-2 rounded-lg transition-colors ${
                activeReport === 'revenue' 
                  ? 'bg-green-100 text-green-600' 
                  : 'bg-gray-100 text-gray-600 group-hover:bg-green-100 group-hover:text-green-600'
              }`}>
                <TrendingUp className="w-5 h-5" />
              </div>
              <span className="text-sm font-semibold">Revenue Trends</span>
              <span className="text-xs text-gray-500">Income Analysis</span>
            </div>
            {activeReport === 'revenue' && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></div>
            )}
          </button>
          
          <button
            onClick={() => setActiveReport('expenses')}
            className={`group relative p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg ${
              activeReport === 'expenses'
                ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-orange-100 text-orange-700 shadow-md'
                : 'border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:bg-orange-50'
            }`}
          >
            <div className="flex flex-col items-center space-y-2">
              <div className={`p-2 rounded-lg transition-colors ${
                activeReport === 'expenses' 
                  ? 'bg-orange-100 text-orange-600' 
                  : 'bg-gray-100 text-gray-600 group-hover:bg-orange-100 group-hover:text-orange-600'
              }`}>
                <PieChart className="w-5 h-5" />
              </div>
              <span className="text-sm font-semibold">Expense Reports</span>
              <span className="text-xs text-gray-500">Cost Breakdown</span>
            </div>
            {activeReport === 'expenses' && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full"></div>
            )}
          </button>
          
          <button
            onClick={() => setActiveReport('pl')}
            className={`group relative p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg ${
              activeReport === 'pl'
                ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-purple-100 text-purple-700 shadow-md'
                : 'border-gray-200 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50'
            }`}
          >
            <div className="flex flex-col items-center space-y-2">
              <div className={`p-2 rounded-lg transition-colors ${
                activeReport === 'pl' 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-600 group-hover:bg-purple-100 group-hover:text-purple-600'
              }`}>
                <FileText className="w-5 h-5" />
              </div>
              <span className="text-sm font-semibold">P&L Statement</span>
              <span className="text-xs text-gray-500">Profit & Loss</span>
            </div>
            {activeReport === 'pl' && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full"></div>
            )}
          </button>
        </div>
      </div>

      {/* Dynamic Content Based on Active Report */}
      {activeReport === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Trend Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-primary-600" />
                <span className="text-sm text-gray-600">Current Period</span>
              </div>
            </div>
            <div className="h-64 flex items-end justify-between space-x-1">
              {revenueData.slice(-6).map((data, index) => (
                <div key={index} className="flex flex-col items-center space-y-2 flex-1">
                  <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '200px' }}>
                    <div 
                      className="bg-primary-500 rounded-t-lg w-full"
                      style={{ 
                        height: `${(data.totalRevenue / Math.max(...revenueData.map(d => d.totalRevenue))) * 100}%`,
                        minHeight: '20px'
                      }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-600 font-medium">
                    {'day' in data ? data.day : 'week' in data ? data.week : 'month' in data ? data.month : data.quarter}
                  </span>
                  <span className="text-xs text-gray-500">{formatCurrency(data.totalRevenue)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Expense Breakdown */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Expense Breakdown</h3>
              <div className="flex items-center space-x-2">
                <PieChart className="w-5 h-5 text-primary-600" />
                <span className="text-sm text-gray-600">Current Period</span>
              </div>
            </div>
            <div className="space-y-4">
              {expenseData.map((expense, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${expense.color}`}></div>
                    <span className="text-sm font-medium text-gray-700">{expense.category}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">{formatCurrency(expense.amount)}</div>
                    <div className="text-xs text-gray-500">{expense.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeReport === 'revenue' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Analysis</h3>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-primary-600" />
              <span className="text-sm text-gray-600">Detailed Revenue Breakdown</span>
            </div>
          </div>
          <div className="h-80 flex items-end justify-between space-x-2">
            {revenueData.map((data, index) => (
              <div key={index} className="flex flex-col items-center space-y-2 flex-1">
                <div className="w-full space-y-1" style={{ height: '300px' }}>
                  <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '100px' }}>
                    <div 
                      className="bg-blue-500 rounded-t-lg w-full"
                      style={{ 
                        height: `${(data.roomRevenue / Math.max(...revenueData.map(d => d.roomRevenue))) * 100}%`,
                        minHeight: '10px'
                      }}
                    ></div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '100px' }}>
                    <div 
                      className="bg-green-500 rounded-t-lg w-full"
                      style={{ 
                        height: `${(data.foodRevenue / Math.max(...revenueData.map(d => d.foodRevenue))) * 100}%`,
                        minHeight: '10px'
                      }}
                    ></div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '100px' }}>
                    <div 
                      className="bg-purple-500 rounded-t-lg w-full"
                      style={{ 
                        height: `${(data.otherRevenue / Math.max(...revenueData.map(d => d.otherRevenue))) * 100}%`,
                        minHeight: '10px'
                      }}
                    ></div>
                  </div>
                </div>
                <span className="text-xs text-gray-600 font-medium">
                  {'day' in data ? data.day : 'week' in data ? data.week : 'month' in data ? data.month : data.quarter}
                </span>
                <span className="text-xs text-gray-500">{formatCurrency(data.totalRevenue)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span className="text-sm text-gray-600">Room Revenue</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span className="text-sm text-gray-600">Food & Beverage</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-purple-500 rounded"></div>
              <span className="text-sm text-gray-600">Other Revenue</span>
            </div>
          </div>
        </div>
      )}

      {activeReport === 'expenses' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Expense Analysis</h3>
            <div className="flex items-center space-x-2">
              <PieChart className="w-5 h-5 text-primary-600" />
              <span className="text-sm text-gray-600">Detailed Expense Breakdown</span>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              {expenseData.map((expense, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${expense.color}`}></div>
                    <span className="text-sm font-medium text-gray-700">{expense.category}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">{formatCurrency(expense.amount)}</div>
                    <div className="text-xs text-gray-500">{expense.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center">
              <div className="text-center">
                <div className="w-48 h-48 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-gray-500">Pie Chart Visualization</span>
                </div>
                <p className="text-sm text-gray-600 mt-4">Visual representation of expense distribution</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeReport === 'pl' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Profit & Loss Statement</h3>
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-primary-600" />
              <span className="text-sm text-gray-600">Annual Report</span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Revenue Section */}
            <div>
              <h4 className="text-md font-semibold text-gray-800 mb-4">Revenue</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Room Revenue</span>
                  <span className="font-semibold">{formatCurrency(plData.revenue.roomRevenue)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Food & Beverage</span>
                  <span className="font-semibold">{formatCurrency(plData.revenue.foodRevenue)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Spa & Wellness</span>
                  <span className="font-semibold">{formatCurrency(plData.revenue.spaRevenue)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Other Revenue</span>
                  <span className="font-semibold">{formatCurrency(plData.revenue.otherRevenue)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-gray-50 rounded-lg px-4">
                  <span className="font-semibold text-gray-900">Total Revenue</span>
                  <span className="font-bold text-lg text-green-600">{formatCurrency(plData.revenue.totalRevenue)}</span>
                </div>
              </div>
            </div>

            {/* Expenses Section */}
            <div>
              <h4 className="text-md font-semibold text-gray-800 mb-4">Expenses</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Payroll & Benefits</span>
                  <span className="font-semibold">{formatCurrency(plData.expenses.payroll)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Utilities</span>
                  <span className="font-semibold">{formatCurrency(plData.expenses.utilities)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Maintenance</span>
                  <span className="font-semibold">{formatCurrency(plData.expenses.maintenance)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Marketing</span>
                  <span className="font-semibold">{formatCurrency(plData.expenses.marketing)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Supplies</span>
                  <span className="font-semibold">{formatCurrency(plData.expenses.supplies)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Insurance</span>
                  <span className="font-semibold">{formatCurrency(plData.expenses.insurance)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Other Expenses</span>
                  <span className="font-semibold">{formatCurrency(plData.expenses.otherExpenses)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-gray-50 rounded-lg px-4">
                  <span className="font-semibold text-gray-900">Total Expenses</span>
                  <span className="font-bold text-lg text-red-600">{formatCurrency(plData.expenses.totalExpenses)}</span>
                </div>
              </div>
            </div>

            {/* Profit Section */}
            <div>
              <h4 className="text-md font-semibold text-gray-800 mb-4">Profit Analysis</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Gross Profit</span>
                  <span className="font-semibold text-green-600">{formatCurrency(plData.profit.grossProfit)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-red-50 rounded-lg px-4">
                  <span className="font-bold text-gray-900">Net Profit</span>
                  <span className={`font-bold text-lg ${plData.profit.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(plData.profit.netProfit)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 bg-gray-50 rounded-lg px-4">
                  <span className="font-semibold text-gray-700">Profit Margin</span>
                  <span className={`font-bold ${plData.profit.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {plData.profit.profitMargin.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceAndAccounting;
